import { cn } from '@myosh/odin-components';
import { memo, useCallback, useMemo } from 'react';
import type { MoveableNodeTypes, VerticallyMoveableNodeType } from '../../../../helpers/node-util';
import { useAppDispatch } from '../../../../redux/hooks';
import { moveNode } from '../../../../redux/slices/diagram';
import ArrowDownIcon from '../icons/arrow-down-icon.component';
import ArrowUpIcon from '../icons/arrow-up-icon.component';

export const VERTICAL_STEP = 1;

export const VerticalMoveDirection = {
  UP: 0,
  DOWN: 1,
} as const;
export type VerticalMoveDirection = (typeof VerticalMoveDirection)[keyof typeof VerticalMoveDirection];

interface VerticalMoveControlProps {
  id: string;
  type: VerticallyMoveableNodeType;
  index: number;
  title: string;
  direction: VerticalMoveDirection;
  disabled: boolean;
}

const VerticalMoveControl = ({ id, type, index, direction, title, disabled }: VerticalMoveControlProps) => {
  const dispatch = useAppDispatch();

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (disabled) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      dispatch(
        moveNode({
          id,
          type: type as MoveableNodeTypes,
          currentIndex: index,
          targetIndex: direction === VerticalMoveDirection.UP ? index - VERTICAL_STEP : index + VERTICAL_STEP,
        })
      );
    },
    [index, direction, disabled]
  );

  const icon = useMemo(
    () => (direction === VerticalMoveDirection.UP ? <ArrowUpIcon /> : <ArrowDownIcon />),
    [direction]
  );

  const dataCyMoveControl = useMemo(() => {
    const directionStr = direction === VerticalMoveDirection.UP ? 'up' : 'down';
    return `vertical-move-control-${directionStr}-${index}`;
  }, [direction, index]);

  return (
    <div
      data-cy={dataCyMoveControl}
      title={title}
      className={cn(
        'nopan nodrag invisible absolute left-[calc(50%-12px)] cursor-pointer group-hover:visible',
        { '-top-6': direction === VerticalMoveDirection.UP },
        { '-bottom-6': direction === VerticalMoveDirection.DOWN },
        { hidden: disabled }
      )}
      onClick={handleClick}
    >
      {icon}
    </div>
  );
};

export default memo(VerticalMoveControl);
